























































































import { Prop, Component, Vue, Watch } from 'vue-property-decorator';
import { Button, Form, FormItem, RadioButton, RadioGroup } from 'element-ui';
import Container from '@/components/inquiry/quote/settings/Container.vue';
import Additional from '@/components/inquiry/quote/settings/Additional.vue';
import MeasureOverride from '@/components/inquiry/quote/settings/MeasureOverride.vue';
import Drayage from '@/components/inquiry/quote/settings/Drayage.vue';
import ExtraBillItems from '@/components/inquiry/quote/settings/ExtraBillItems.vue';
import Route from '@/components/inquiry/quote/settings/Route.vue';
import Trucking from '@/components/inquiry/quote/settings/Trucking.vue';
import {
  CargoTypeEnum,
  InquiryFields,
  InquiryFields_arrivalAddress,
  InquiryInput,
  PlaceFields,
  RequestedContainerInput,
  TransportationTypeEnum,
  fetchQuotes_inquiries_quotes,
  fetchQuotes_inquiries_quotes_inquiry,
  upsertQuote_upsertQuote_inquiry,
  TransportationOptionInput,
  TruckingDetailInput,
  TransportationOptionFields,
  CargoDescriptionInput,
  BillItemInput
} from '@/types/graphql/schemaTypes';
import { RouteInput } from '@/types/types';
import { i18n } from '@/plugins/i18n';
import get from 'lodash/get';

@Component({
  components: {
    Additional,
    MeasureOverride,
    Container,
    Drayage,
    ExtraBillItems,
    Route,
    Trucking,
    'el-button': Button,
    'el-form': Form,
    'el-form-item': FormItem,
    'el-radio-button': RadioButton,
    'el-radio-group': RadioGroup
  }
})
export default class QuoteSettings extends Vue {
  @Prop({ required: true }) private inquiry!: fetchQuotes_inquiries_quotes_inquiry;
  @Prop({ required: true }) private quote!: fetchQuotes_inquiries_quotes;

  private inquiryInput: InquiryInput = {
    arrivalAddress: null,
    cargoDescription: {
      containers: null,
      parcels: null,
      description: '',
      totalVolume: null,
      totalWeight: null
    },
    cargoType: null,
    departureAddress: null,
    id: '',
    transportationType: null,
    truckingDetails: null
  };

  private extraBillItems: BillItemInput[] = [];
  private marginInput = 0;
  private transportationMode = 'seaLCL';

  private formatPort(port: PlaceFields): string | null {
    if (port) {
      const code = `${port.countryCode}${port.code}`;
      let i18nKey = 'ports.place-type.select-label.port';
      if (port.placeType === 'airport') {
        i18nKey = 'ports.place-type.select-label.airport';
      }

      return this.$t(i18nKey, { code, name: port.name }).toString();
    }
    return null;
  }

  /**
   * NOTE: in case we need to remove parcels or containers depending on cargoType
   * remove the key on submit below
   */
  private submit() {
    this.$store.dispatch('Inquiry/upsertQuote', {
      shipmentId: this.$route.params.id || null,
      quoteId: this.quote.id,
      inquiryArgs: this.inquiryInput,
      extraBillItems: this.extraBillItems,
      margin: !this.marginInput ? null : this.marginInput
    }).then(() => {
      this.$store.dispatch('Alert/addAlert', {
        type: 'success',
        message: i18n.t('success.created'),
        time: 5000
      });
      if (this.$store.state.Inquiry.isSimulation) {
        this.$emit('refresh');
      }
    });
  }

  private arrivalAddress(inquiry: fetchQuotes_inquiries_quotes_inquiry) {
    if (inquiry.arrivalAddress) {
      return inquiry.arrivalAddress!.formattedAddress;
    }
    return null;
  }

  private deleteQuote() {
    this.$store.dispatch('Inquiry/deleteQuote', this.quote.id).then(() => {
      this.$emit('delete');
    });
  }

  private departureAddress(inquiry: fetchQuotes_inquiries_quotes_inquiry) {
    if (inquiry.departureAddress) {
      return inquiry.departureAddress!.formattedAddress;
    }
    return null;
  }

  private updateAdditional(data: number) {
    this.marginInput = data;
  }

  private updateContainer(data: RequestedContainerInput[]) {
    this.inquiryInput.cargoDescription!.containers = data;
  }

  private updateDrayage(data: TransportationOptionInput, key: keyof CargoDescriptionInput) {
    if (this.inquiryInput.cargoDescription) {
      (this.inquiryInput.cargoDescription as any)[key] = data;
    }
  }

  private updateMeasures(data: { totalWeight?: number | null; totalVolume?: number | null }) {
    this.inquiryInput.cargoDescription = { ...this.inquiryInput.cargoDescription, ...data };
  }

  private updateExtraCost(data: BillItemInput[]) {
    this.extraBillItems = data;
  }

  private updateRoute(data: RouteInput) {
    this.inquiryInput.arrivalAddress = data.arrivalAddress;
    this.inquiryInput.arrivalPlaceId = data.arrivalPlaceId;
    this.inquiryInput.departureAddress = data.departureAddress;
    this.inquiryInput.departurePlaceId = data.departurePlaceId;
  }

  private updateTrucking(data: TruckingDetailInput, key: ('departureOptions' | 'arrivalOptions')) {
    if (this.inquiryInput.cargoDescription) {
      this.inquiryInput.cargoDescription[key]!.truckingInformations! = data;
    }
  }

  private toContainersInput(inquiry: InquiryFields) {
    if (inquiry.cargoDescription!.containers) {
      return (inquiry.cargoDescription!.containers || []).map((container: any) => {
        return {
          contentNetWeight: container.contentNetWeight || null,
          requestedContainerCount: container.requestedContainerCount || null,
          requestedContainerType: container.requestedContainerType || null
        };
      });
    }
  }

  /**
   * not part of the form
   * here to keep the inquiry's snapshot identical with original inquiry
   * in case we use it and replace actual inquiry with the edited copy
   */
  private toParcelsInput(inquiry: InquiryFields) {
    if (inquiry.cargoDescription!.parcels) {
      return (inquiry.cargoDescription!.parcels || []).map((parcel: any) => {
        return {
          shipmentId: parcel.shipmentId || null,
          description: parcel.description || null,
          currency: parcel.currency || null,
          sizeUnit: parcel.sizeUnit || null,
          grossUnit: parcel.grossUnit || null,
          weightUnit: parcel.weightUnit || null,
          volumeUnit: parcel.volumeUnit || null,
          length: parcel.length || null,
          width: parcel.width || null,
          height: parcel.height || null,
          volume: parcel.volume || null,
          netWeight: parcel.netWeight || null,
          grossWeight: parcel.grossWeight || null,
          parcelValue: parcel.parcelValue || null,
          packagingType: parcel.packagingType || null,
          hsCode: parcel.hsCode || null,
          quantity: parcel.quantity || null,
          productQuantity: parcel.productQuantity || null,
          productPrice: parcel.productPrice || null,
          productId: parcel.productId || null,
          onPallet: parcel.onPallet || null
        };
      });
    }
  }

  private toCargoDescriptionOptionsInput(options: TransportationOptionFields | null) {
    if (!options) {
      return {
        drayage: false,
        truckingInformations: {
          truckingArrangement: false,
          truckType: null,
          truckCount: null,
          cargoForkliftable: null,
          cargoStackable: null,
          cargoStackableSize: null
        },
        vanningInformations: {
          needsVanning: false,
          cost: 0,
          currency: 'JPY'
        }
      };
    }
    return {
      drayage: options.drayage || false,
      truckingInformations: {
        truckingArrangement: options.truckingInformations!.truckingArrangement || false,
        truckType: options.truckingInformations!.truckType || null,
        truckCount: options.truckingInformations!.truckCount || null,
        cargoForkliftable: options.truckingInformations!.cargoForkliftable || null,
        cargoStackable: options.truckingInformations!.cargoStackable || null,
        cargoStackableSize: options.truckingInformations!.cargoStackableSize || null
      },
      vanningInformations: {
        needsVanning: options.vanningInformations!.needsVanning || false,
        cost: options.vanningInformations!.cost || 0,
        currency: options.vanningInformations!.currency || 'JPY'
      }
    };
  }

  get arrivalOptions() {
    return get(this.inquiry, 'cargoDescription.arrivalOptions') || null;
  }

  get departureOptions() {
    return get(this.inquiry, 'cargoDescription.departureOptions') || null;
  }

  get arrivalTrucking() {
    return get(this.inquiry, 'cargoDescription.arrivalOptions.truckingInformations') || null;
  }

  get departureTrucking() {
    return get(this.inquiry, 'cargoDescription.departureOptions.truckingInformations') || null;
  }

  @Watch('inquiry', { immediate: true })
  setInputs(inquiry: InquiryFields) {
    if (!inquiry) {
      return;
    }
    this.inquiryInput = {
      arrivalPlaceId: inquiry.arrivalPlace ? inquiry.arrivalPlace.id : null,
      cargoDescription: {
        description: inquiry.cargoDescription!.description || '',
        containers: this.toContainersInput(inquiry),
        parcels: this.toParcelsInput(inquiry),
        arrivalOptions: this.toCargoDescriptionOptionsInput(
          inquiry.cargoDescription!.arrivalOptions
        ),
        departureOptions: this.toCargoDescriptionOptionsInput(
          inquiry.cargoDescription!.departureOptions
        )
      },
      cargoType: inquiry.cargoType || null,
      departurePlaceId: inquiry.departurePlace ? inquiry.departurePlace.id : null,
      id: inquiry.id || '',
      transportationType: inquiry.transportationType || null,
      truckingDetails: inquiry.truckingDetails || null
    };
    this.transportationMode = inquiry.transportationType === TransportationTypeEnum.sea
      ? `${inquiry.transportationType}${inquiry.cargoType}` : inquiry.transportationType || '';
    this.marginInput = this.quote.margin || 0;
  }

  @Watch('transportationMode')
  setTransportationMode(transportationMode: 'air' | 'seaFCL' | 'seaLCL') {
    switch (transportationMode) {
      case 'seaLCL':
        this.inquiryInput.cargoType = CargoTypeEnum.LCL;
        this.inquiryInput.transportationType = TransportationTypeEnum.sea;
        break;
      case 'seaFCL':
        this.inquiryInput.cargoType = CargoTypeEnum.FCL;
        this.inquiryInput.transportationType = TransportationTypeEnum.sea;
        break;
      case 'air':
        this.inquiryInput.transportationType = TransportationTypeEnum.air;
        break;
    }
  }
}
